// "Set ayarları" küçük paneli + "İşletmeler" salt okunur özet kartı (Set Yönetimi sol sütun):
// ad / çerçeve (TODO-KPI-29, frameworks tablosundan) / tablo düzeni (K27 etiketleri) → PATCH; set silme ConfirmDialog ile
// ("değerler havuzda kalır"). İşletme sayısı `listClientEntities` ile çekilir, yönetim profilde.
// Kırılım (§6.7): "Raporda kırılımları göster" anahtarı `report_breakdowns`'u hemen PATCH'ler
// (Kaydet beklemez; varsayılan açık — DTO alanı yalnız kapalıyken gelir).
// Sektör paketleri DR10: "Bu setten paket oluştur" (kpi.manage) → PackageFromSetDialog.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  KPI_REPORT_TABLE_LAYOUTS,
  KPI_REPORT_TABLE_LAYOUT_LABELS,
  type KpiReportTableLayout,
  type KpiSet,
} from "@mahir/shared";
import { listClientEntities } from "../../api/client-entities";
import { deleteKpiSet, setKpiReportBreakdowns, updateKpiSet } from "../../api/kpi";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { KPI_COPY } from "../../lib/kpi-ui";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { FrameworkSelect } from "./FrameworkSelect";
import { PackageFromSetDialog } from "./PackageFromSetDialog";
import {
  btnDanger,
  btnGhost,
  btnPrimary,
  errorBox,
  fieldStyle,
  inputStyle,
  labelStyle,
} from "./kpi-form-styles";

export interface SetSettingsPanelProps {
  set: KpiSet;
  canEdit: boolean;
  onUpdated: (set: KpiSet) => void;
  onDeleted: () => void;
  /** müşteri sektörü — "Bu setten paket oluştur" sektör ön seçimi */
  clientSector?: string | null | undefined;
}

export function SetSettingsPanel(props: SetSettingsPanelProps): JSX.Element {
  const { set, canEdit, onUpdated, onDeleted } = props;
  const [name, setName] = useState(set.name);
  const [frameworkId, setFrameworkId] = useState<string | null>(set.frameworkId);
  const [layout, setLayout] = useState<KpiReportTableLayout>(set.reportTableLayout);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [fromSetOpen, setFromSetOpen] = useState(false);
  const reportBreakdowns = set.reportBreakdowns !== false;

  useEffect(() => {
    setName(set.name);
    setFrameworkId(set.frameworkId);
    setLayout(set.reportTableLayout);
    setError(null);
  }, [set.id, set.name, set.frameworkId, set.reportTableLayout]);

  const dirty =
    name.trim() !== set.name || frameworkId !== set.frameworkId || layout !== set.reportTableLayout;

  async function save(): Promise<void> {
    if (!dirty || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateKpiSet(set.id, {
        ...(name.trim() !== set.name ? { name: name.trim() } : {}),
        ...(frameworkId !== set.frameworkId ? { frameworkId } : {}),
        ...(layout !== set.reportTableLayout ? { reportTableLayout: layout } : {}),
      });
      onUpdated(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  }

  async function toggleReportBreakdowns(enabled: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      onUpdated(await setKpiReportBreakdowns(set.id, enabled));
    } catch {
      setError(KPI_BREAKDOWN_COPY.reportToggleFailed);
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deleteKpiSet(set.id);
      setConfirmDelete(false);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Silinemedi");
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-label="Veri seti ayarları"
      className="rounded-xl p-4"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <div style={{ ...labelStyle, marginBottom: 8 }}>VERİ SETİ AYARLARI</div>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="set-name">
          Ad
        </label>
        <input
          id="set-name"
          style={inputStyle}
          value={name}
          disabled={!canEdit || busy}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="set-framework">
          {KPI_COPY.framework.label}
        </label>
        <FrameworkSelect
          id="set-framework"
          value={frameworkId}
          currentName={set.frameworkName}
          disabled={!canEdit || busy}
          onChange={(next) => setFrameworkId(next.id)}
        />
        <div className="text-xs" style={{ color: "var(--color-text-3)", marginTop: 4 }}>
          {KPI_COPY.framework.hint}
        </div>
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="set-layout">
          Tablo düzeni
        </label>
        <select
          id="set-layout"
          style={inputStyle}
          value={layout}
          disabled={!canEdit || busy}
          onChange={(e) => setLayout(e.target.value as KpiReportTableLayout)}
        >
          {KPI_REPORT_TABLE_LAYOUTS.map((l) => (
            <option key={l} value={l}>
              {KPI_REPORT_TABLE_LAYOUT_LABELS[l]}
            </option>
          ))}
        </select>
      </div>
      <div style={fieldStyle}>
        <label
          className="flex items-center gap-2 text-sm"
          style={{ color: "var(--color-text)", minHeight: 44 }}
        >
          <input
            type="checkbox"
            data-testid="set-report-breakdowns"
            checked={reportBreakdowns}
            disabled={!canEdit || busy}
            aria-describedby="set-report-breakdowns-help"
            onChange={(e) => void toggleReportBreakdowns(e.target.checked)}
          />
          {KPI_BREAKDOWN_COPY.reportToggle}
        </label>
        <div
          id="set-report-breakdowns-help"
          className="text-xs"
          style={{ color: "var(--color-text-3)" }}
        >
          {KPI_BREAKDOWN_COPY.reportToggleHelp}
        </div>
      </div>
      {error ? (
        <div style={{ ...errorBox, marginBottom: 10 }} role="alert">
          {error}
        </div>
      ) : null}
      {canEdit ? (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            style={{ ...btnPrimary, opacity: dirty ? 1 : 0.5 }}
            disabled={!dirty || busy || !name.trim()}
            onClick={() => void save()}
          >
            Kaydet
          </button>
          <button
            type="button"
            style={btnDanger}
            disabled={busy}
            onClick={() => setConfirmDelete(true)}
          >
            Veri setini sil
          </button>
          <button
            type="button"
            style={{ ...btnGhost, minHeight: 44 }}
            disabled={busy}
            onClick={() => setFromSetOpen(true)}
          >
            {KPI_PACKAGES_COPY.fromSet.action}
          </button>
        </div>
      ) : null}
      <PackageFromSetDialog
        open={fromSetOpen}
        set={set}
        clientSector={props.clientSector}
        onClose={() => setFromSetOpen(false)}
      />
      <ConfirmDialog
        open={confirmDelete}
        title="Veri seti silinsin mi?"
        body={`"${set.name}" veri seti silinir. Değerler havuzda kalır — başka bir veri seti aynı göstergeleri içerirse değerler orada görünmeye devam eder.`}
        confirmLabel="Veri setini sil"
        tone="danger"
        busy={busy}
        onConfirm={() => void remove()}
        onCancel={() => setConfirmDelete(false)}
      />
    </section>
  );
}

export function EntitiesSummaryCard({ clientId }: { clientId: string }): JSX.Element {
  const [count, setCount] = useState<number | null>(null);
  const [names, setNames] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    setCount(null);
    listClientEntities(clientId)
      .then((list) => {
        if (cancelled) return;
        const active = list.filter((e) => e.isActive);
        setCount(active.length);
        setNames(active.slice(0, 4).map((e) => (e.isDefault ? `${e.name} (varsayılan)` : e.name)));
      })
      .catch(() => {
        if (!cancelled) setCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  return (
    <section
      aria-label="İşletmeler"
      className="rounded-xl p-4"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <div style={{ ...labelStyle, marginBottom: 6 }}>
        İŞLETMELER{count !== null ? ` (${count})` : ""}
      </div>
      {count === null ? (
        <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
          Yükleniyor…
        </div>
      ) : (
        <ul className="text-sm" style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {names.map((n) => (
            <li key={n} style={{ color: "var(--color-text)" }}>
              {n}
            </li>
          ))}
          {count > names.length ? (
            <li className="text-xs" style={{ color: "var(--color-text-3)" }}>
              +{count - names.length} daha
            </li>
          ) : null}
          {count === 0 ? (
            <li className="text-xs" style={{ color: "var(--color-text-3)" }}>
              İlk değer girildiğinde varsayılan işletme açılır.
            </li>
          ) : null}
        </ul>
      )}
      <Link
        to={`/clients/${clientId}/entities`}
        className="text-sm"
        style={{
          color: "var(--color-primary)",
          display: "inline-block",
          marginTop: 8,
          minHeight: 32,
        }}
      >
        Müşteri profilinde yönet →
      </Link>
    </section>
  );
}
